const normsConst = {
  1: {
    items: [
      {
        n: 171,
        q: 1,
        std: 1.8046113008136748,
        average: 8.046783625730994,
      },
      {
        n: 171,
        q: 2,
        std: 1.8611410180942458,
        average: 7.970760233918129,
      },
      {
        n: 171,
        q: 3,
        std: 1.6022430098267553,
        average: 7.87719298245614,
      },
    ],
    overall_std: 1.9560515457422112,
    overall_average: 7.760330578512397,
  },
  2: {
    items: [
      {
        n: 171,
        q: 1,
        std: 2.3763685789411997,
        average: 7.076023391812866,
      },
    ],
    overall_std: 2.3763685789411997,
    overall_average: 7.076023391812866,
  },
  3: {
    items: [],
    overall_std: 1.7574442231173146,
    overall_average: 7.964912280701754,
  },
  4: {
    items: [
      {
        n: 57,
        q: "1",
        std: 2.621962378981052,
        average: 6.350877192982456,
      },
    ],
    overall_std: 2.621962378981052,
    overall_average: 6.350877192982456,
  },
};

/**
 * 1) Helper function to build the OUTCOMES breakdown
 *    This mirrors the "buildQuestionBreakdown" logic, but for outcome questions.
 */
function buildOutcomesBreakdown(
  allResponses,
  outcomeQuestions,
  anchorCategory,
  norms
) {
  // --------------------------------------------------
  // (A) Parse outcome questions into a lookup keyed by (q, s)
  // --------------------------------------------------
  let outcomeLookup = {};
  outcomeQuestions?.questions?.forEach((outcomeBlock) => {
    const outcomeId = outcomeBlock.id; // e.g. "1", "2"
    const outcomeName = outcomeBlock.name; // e.g. "Well-being", "Retention"
    const questionList = outcomeBlock.questions; // array of question definitions

    questionList.forEach((oq, idx) => {
      // Build a key like "1-1"
      const key = `${oq.q}-${oq.s}`;
      outcomeLookup[key] = {
        outcomeId,
        outcomeName,
        questionIndex: idx, // Index within this outcome block
        questionText: oq.question,
      };
    });
  });

  // --------------------------------------------------
  // (B) Build aggregator map for outcomes with roles aggregated:
  // outcomeKey = `${outcomeId}-${questionIndex}`
  // --------------------------------------------------
  let outcomeMap = {};

  allResponses.forEach((entry) => {
    if (!entry._outcomeKey) return;

    // Find the outcome definition from our lookup
    const outcomeDef = outcomeLookup[entry._outcomeKey];
    if (!outcomeDef) return;

    const { outcomeId, outcomeName, questionIndex, questionText } = outcomeDef;
    const outcomeKey = `${outcomeId}-${questionIndex}`;

    // Create an aggregator for this outcome question if needed
    if (!outcomeMap[outcomeKey]) {
      outcomeMap[outcomeKey] = {
        outcomeTitle: outcomeName,
        questionIndex,
        questionText,
        distribution: new Array(entry.distribution.length).fill(0),
        n: 0,
        rawValues: [], // Store all raw response values for accurate overall calculation
        roles: [], // Holds aggregated role data
        normAverage:
          norms?.[outcomeId]?.items?.find(
            (item) => item.q === Number(questionIndex + 1)
          )?.average || null,
      };
    }

    // Compute the norm difference (overall average - norm)
    if (outcomeMap[outcomeKey].normAverage !== null) {
      outcomeMap[outcomeKey].normDifference =
        entry.average - outcomeMap[outcomeKey].normAverage;
    }

    // Update overall distribution, count, and raw values for this outcome question
    outcomeMap[outcomeKey].n += entry.n;
    // Store the actual raw response value (since each entry has n=1, entry.average is the raw score)
    outcomeMap[outcomeKey].rawValues.push(entry.average);
    entry.distribution.forEach((count, i) => {
      outcomeMap[outcomeKey].distribution[i] += count;
    });

    // Identify role details using anchorCategory options
    const matchedOption = anchorCategory?.options?.find(
      (opt) => opt.id === entry.role
    );
    const roleId = entry.role;
    const roleName = matchedOption?.name || entry.role;
    const roleColor = matchedOption?.color || "#CCCCCC";

    // Aggregate by role: check if this role already exists in the roles array.
    let roleAggregator = outcomeMap[outcomeKey].roles.find(
      (r) => r.roleId === roleId
    );
    if (!roleAggregator) {
      roleAggregator = {
        roleId,
        roleName,
        color: roleColor,
        total: 0, // Sum of (average * n)
        n: 0, // Total count of responses for this role
        distribution: new Array(entry.distribution.length).fill(0),
      };
      outcomeMap[outcomeKey].roles.push(roleAggregator);
    }

    // Since each entry is a single response (n = 1), we update:
    roleAggregator.total += entry.average * entry.n;
    roleAggregator.n += entry.n;
    entry.distribution.forEach((count, i) => {
      roleAggregator.distribution[i] += count;
    });
  });

  // --------------------------------------------------
  // (C) Compute overallAverage & overallStdev for each outcome question
  // and compute role-level averages and stdevs.
  // --------------------------------------------------
  Object.values(outcomeMap).forEach((oItem) => {
    const totalResponses = oItem.rawValues.length;
    oItem.overallN = totalResponses;

    if (totalResponses === 0) {
      oItem.overallAverage = 0;
      oItem.overallStdev = 0;
    } else {
      // Calculate overall average directly from raw response values
      const sum = oItem.rawValues.reduce((acc, val) => acc + val, 0);
      const mean = sum / totalResponses;
      
      // Calculate standard deviation directly from raw values
      const varianceSum = oItem.rawValues.reduce((acc, val) => {
        const diff = val - mean;
        return acc + (diff * diff);
      }, 0);
      
      oItem.overallAverage = mean;
      oItem.overallStdev = Math.sqrt(varianceSum / totalResponses);
    }

    // Compute aggregates for each role in this outcome question
    oItem.roles.forEach((roleAgg) => {
      roleAgg.average = roleAgg.n > 0 ? roleAgg.total / roleAgg.n : 0;
      const roleTotal = roleAgg.distribution.reduce((a, b) => a + b, 0);
      if (roleTotal === 0) {
        roleAgg.stdev = 0;
      } else {
        let sum = 0;
        roleAgg.distribution.forEach((count, score) => {
          sum += score * count;
        });
        const mean = sum / roleTotal;
        let varianceSum = 0;
        roleAgg.distribution.forEach((count, score) => {
          if (count > 0) {
            const diff = score - mean;
            varianceSum += diff * diff * count;
          }
        });
        roleAgg.stdev = Math.sqrt(varianceSum / roleTotal);
      }
    });

    // --------------------------------------------------
    // (NEW) Apply rule of 3: Filter out roles with less than 3 respondents.
    // --------------------------------------------------
    oItem.roles = oItem.roles.filter((role) => role.n >= 3);

    // **Sort roles by average descending**
    oItem.roles.sort((a, b) => b.average - a.average);
  });

  // --------------------------------------------------
  // (D) Convert outcomeMap into array grouped by outcomeTitle:
  // [
  //   { outcomeTitle: "Well-being", questions: [ { ... }, { ... } ] },
  //   { outcomeTitle: "Retention", questions: [ ... ] }
  // ]
  // --------------------------------------------------
  let groupedByOutcomeTitle = {};
  Object.values(outcomeMap).forEach((item) => {
    const title = item.outcomeTitle;
    if (!groupedByOutcomeTitle[title]) {
      groupedByOutcomeTitle[title] = [];
    }
    groupedByOutcomeTitle[title].push(item);
  });

  let outcomesBreakdown = [];
  Object.keys(groupedByOutcomeTitle).forEach((title) => {
    const questionsArray = groupedByOutcomeTitle[title];
    // Sort questions by questionIndex if needed
    questionsArray.sort((a, b) => a.questionIndex - b.questionIndex);
    outcomesBreakdown.push({
      outcomeTitle: title,
      questions: questionsArray,
    });
  });

  return outcomesBreakdown;
}

/**
 * 2) Helper to build questionBreakdown, but now also includes question-level norms
 */
function buildQuestionBreakdown(
  processedResponses,
  questions,
  anchorCategory,
  norms
) {
  // We'll create a map keyed by dimension-factor-question index:
  // key = `${dimension}-${factor}-${questionIndex}`
  let questionMap = {};

  processedResponses.forEach((entry) => {
    // 1) Identify dimension/factor/question objects from your `questions` structure
    const dimObj = questions?.dimensions?.[entry.dimension];
    const factorObj = dimObj?.factors?.[entry.factor];
    const questionObj = factorObj?.questions?.[entry.question];

    if (!dimObj || !factorObj || !questionObj) {
      return; // skip if we can't find a matching dimension/factor/question
    }

    // We'll store the real dimension ID & factor ID so we can build the norm key
    // from dimensionObj.id - 1, factorObj.id - 1, and questionIndex
    // Because you do e.g. dimensionId = dimensionObj.id - 1 in summary array,
    // we'll do the same here:
    const realDimensionId = dimObj.id - 1;
    const realFactorId = factorObj.id - 1;

    // 2) Identify dimension/factor titles
    const dimensionTitle = dimObj.title;
    const factorTitle = factorObj.title;

    // 3) Create or retrieve the aggregator for this dimension-factor-question
    const key = `${entry.dimension}-${entry.factor}-${entry.question}`;
    if (!questionMap[key]) {
      questionMap[key] = {
        dimensionTitle,
        factorTitle,
        questionIndex: entry.question,
        questionText: questionObj.q,
        realDimensionId,
        realFactorId,
        // We'll keep an "overall" distribution for the entire question:
        distribution: new Array(entry.distribution.length).fill(0),
        n: 0,
        rawValues: [], // Store all raw response values for accurate overall calculation
        roles: [],
      };
    }

    // 4) Update "overall" aggregator
    questionMap[key].n += entry.n;
    // Store the actual raw response values from the entry
    if (entry.rawResponses && entry.rawResponses.length > 0) {
      questionMap[key].rawValues.push(...entry.rawResponses);
    }
    entry.distribution.forEach((count, i) => {
      questionMap[key].distribution[i] += count;
    });

    // 5) role details
    const matchedOption = anchorCategory?.options?.find(
      (opt) => opt.id === entry.role
    );
    const roleName = matchedOption?.name || entry.role;
    const roleColor = matchedOption?.color || "#CCCCCC";

    // 6) Add this role-specific data
    questionMap[key].roles.push({
      roleId: entry.role,
      roleName,
      color: roleColor,
      average: entry.average,
      distribution: entry.distribution,
      stdev: entry.stdev,
      n: entry.n,
    });

    // Order roles by average descending
    questionMap[key].roles.sort((a, b) => b.average - a.average);

    // Apply rule of 3 to filter out roles with less than 3 respondents
    questionMap[key].roles = questionMap[key].roles.filter((role) => {
      return role.n >= 3;
    });
  });

  // ------------------------------------------------------
  //  Compute overallAverage & overallStdev for each question
  // ------------------------------------------------------
  Object.values(questionMap).forEach((qItem) => {
    const totalResponses = qItem.rawValues.length;
    qItem.overallN = totalResponses;

    if (totalResponses === 0) {
      qItem.overallAverage = 0;
      qItem.overallStdev = 0;
    } else {
      // Calculate overall average directly from raw response values
      const sum = qItem.rawValues.reduce((acc, val) => acc + val, 0);
      const mean = sum / totalResponses;
      
      // Calculate standard deviation directly from raw values
      const varianceSum = qItem.rawValues.reduce((acc, val) => {
        const diff = val - mean;
        return acc + (diff * diff);
      }, 0);
      const stdev = Math.sqrt(varianceSum / totalResponses);

      qItem.overallAverage = mean;
      qItem.overallStdev = stdev;
    }

    // ------------------------------------------------------
    // (NEW) Look up question-level norm if norms?.questions
    // ------------------------------------------------------
    if (norms?.questions) {
      // Build a string like "101" => dimension=1, factor=0, question=1
      // since you do dimensionObj.id - 1, factorObj.id - 1, questionIndex
      const dimId = qItem.realDimensionId;
      const factId = qItem.realFactorId;
      const questIndex = qItem.questionIndex;
      const normKey = `${dimId}${factId}${questIndex}`; // e.g. "100"

      const questionNorm = norms.questions[normKey];
      if (Array.isArray(questionNorm) && questionNorm.length === 2) {
        // questionNorm[0] => norm average
        // questionNorm[1] => norm stdev
        qItem.normAverage = questionNorm[0];
        qItem.normStdev = questionNorm[1];
        qItem.normDifference = qItem.overallAverage - qItem.normAverage;
      }
    }
  });

  // ------------------------------------------------------
  //  Convert questionMap into dimension→factor→questions arrays
  // ------------------------------------------------------
  let breakdownTemp = {};
  Object.values(questionMap).forEach((item) => {
    const { dimensionTitle, factorTitle } = item;
    if (!breakdownTemp[dimensionTitle]) {
      breakdownTemp[dimensionTitle] = {};
    }
    if (!breakdownTemp[dimensionTitle][factorTitle]) {
      breakdownTemp[dimensionTitle][factorTitle] = [];
    }
    breakdownTemp[dimensionTitle][factorTitle].push(item);
  });

  let questionBreakdown = [];
  Object.keys(breakdownTemp).forEach((dimensionTitle) => {
    let factorArray = [];
    Object.keys(breakdownTemp[dimensionTitle]).forEach((factorTitle) => {
      const questionsArray = breakdownTemp[dimensionTitle][factorTitle];
      questionsArray.sort((a, b) => a.questionIndex - b.questionIndex);

      factorArray.push({
        factorTitle,
        questions: questionsArray,
      });
    });

    questionBreakdown.push({
      dimensionTitle,
      factors: factorArray,
    });
  });

  return questionBreakdown;
}

/**
 * For each 'parent' in selectedFilters, we require that the response
 * has a category object with `id === parent` and a `response` that matches
 * one of the filter IDs for that parent.
 * If selectedFilters is empty, we accept everything.
 */
function filterResponseBySelectedFilters(response, selectedFilters) {
  // If no filters, allow everything
  if (!selectedFilters || selectedFilters.length === 0) {
    return true;
  }

  // 1) Group selected filters by their 'parent'
  //    e.g. { "4254cb39-a0f3-45b1-8c04-ec02a57546d9": new Set(["a09d3eb8-...", "91614406-..."]) }
  const parentMap = {};
  selectedFilters.forEach((f) => {
    if (!parentMap[f.parent]) {
      parentMap[f.parent] = new Set();
    }
    parentMap[f.parent].add(f.id);
  });

  // 2) For each parent, we must find at least one matching category in the response
  //    that has (category.id === parent) and (category.response in the parent's set).
  for (const parentId of Object.keys(parentMap)) {
    const possibleResponseIds = parentMap[parentId]; // set of filter ids for this parent

    // Check if there's at least ONE matching category
    const isMatch = response.categories.some(
      (cat) => cat.id === parentId && possibleResponseIds.has(cat.response)
    );

    if (!isMatch) {
      // This response fails for this parent → exclude
      return false;
    }
  }

  // If we pass all parent checks, it's included
  return true;
}

export const calculateTableData = (
  data,
  selectedFilters,
  structure,
  anchor,
  rule,
  questions,
  outcomeQuestions,
  norms
) => {
  let filters = new Set();
  let anchorCategory = structure.categories.find((cat) => cat.id === anchor);
  if (!anchorCategory) {
    throw new Error("Invalid anchor ID");
  }
  // Use the category options as filters (for breakdown responses)
  anchorCategory.options.forEach((option) => filters.add(option.id));

  // We'll compute two maps:
  // 1) overallResponseMap: all responses (that pass the selected filters) for summary calculations.
  // 2) breakdownResponseMap: only responses with a valid anchor category for the breakdowns.
  let overallResponseMap = {};
  let breakdownResponseMap = {};
  let respondentCount = {};

  let naCount = 0;
  let naCountBreakdown = 0;
  data.forEach((response) => {
    // Only include responses that pass the selectedFilters
    if (!filterResponseBySelectedFilters(response, selectedFilters)) {
      return;
    }


    // --- Process overall responses (all responses that pass the filters) ---
    response.questions.forEach((q) => {
      // Skip 'N/A' responses
      if (q.response === 'N/A' || q.response === null || q.response === undefined) {
        naCount++;
        return;
      }
      
      // Note: no role is appended to the key so that all responses for a given question aggregate together.
      let key = `${q.id}-${q.factor}-${q.q}`;
      if (!(key in overallResponseMap)) {
        overallResponseMap[key] = {
          total: 0,
          n: 0,
          responses: [],
          adjustedTotal: 0,
          adjustedResponses: [],
          question: q.q,
          factor: q.factor,
          dimension: q.id,
          stdev: 0,
        };
      }
      let adjustedScore = checkReverseScore(q, questions);
      overallResponseMap[key].adjustedTotal += adjustedScore;
      overallResponseMap[key].adjustedResponses.push(adjustedScore);
      overallResponseMap[key].total += q.response;
      overallResponseMap[key].n += 1;
      overallResponseMap[key].responses.push(q.response);
    });

    // --- Process breakdown responses (only if valid anchor exists) ---
    let category = response.categories.find((cat) => cat.id === anchor);
    if (!category || !filters.has(category.response)) {
      return;
    }
    let role = category.response;
    respondentCount[role] = (respondentCount[role] || 0) + 1;
    response.questions.forEach((q) => {
      // Skip 'N/A' responses
      if (q.response === 'N/A' || q.response === null || q.response === undefined) {
        naCountBreakdown++;
        return;
      }
      
      let key = `${q.id}-${q.factor}-${q.q}-${role}`;
      if (!(key in breakdownResponseMap)) {
        breakdownResponseMap[key] = {
          total: 0,
          n: 0,
          responses: [],
          adjustedTotal: 0,
          adjustedResponses: [],
          role,
          question: q.q,
          factor: q.factor,
          dimension: q.id,
          stdev: 0,
        };
      }
      let adjustedScore = checkReverseScore(q, questions);
      breakdownResponseMap[key].adjustedTotal += adjustedScore;
      breakdownResponseMap[key].adjustedResponses.push(adjustedScore);
      breakdownResponseMap[key].total += q.response;
      breakdownResponseMap[key].n += 1;
      breakdownResponseMap[key].responses.push(q.response);
    });
  });

  // Process the overall responses into an array for summary calculations.
  const processedOverall = Object.values(overallResponseMap).map((entry) => {
    let average = entry.total / entry.n;
    let stdev = getStandardDeviation(entry.responses);
    return {
      average,
      distribution: getBuckets(entry.responses),
      question: entry.question,
      factor: entry.factor,
      dimension: entry.dimension,
      stdev,
      n: entry.n,
    };
  });

  // Process the breakdown responses into an array for the anchor–dependent breakdowns.
  const processedBreakdown = Object.values(breakdownResponseMap).map(
    (entry) => {
      let average = entry.total / entry.n;
      let stdev = getStandardDeviation(entry.responses);
      return {
        average,
        label: anchorCategory.options.find((opt) => opt.id === entry.role),
        distribution: getBuckets(entry.responses),
        role: entry.role,
        question: entry.question,
        factor: entry.factor,
        dimension: entry.dimension,
        stdev,
        n: entry.n,
        rawResponses: entry.responses, // Add raw responses for question breakdown
      };
    }
  );

  // --- Build the overall dimension+factor summary using processedOverall ---
  let dimensionMap = {};
  processedOverall.forEach((entry) => {
    const dimensionObj = questions?.dimensions[entry.dimension];
    const factorObj = dimensionObj?.factors[entry.factor];
    if (!dimensionObj || !factorObj) return;
    const dimensionTitle = dimensionObj.title;
    const factorTitle = factorObj.title;
    const value = entry.average;
    if (!dimensionMap[dimensionTitle]) {
      dimensionMap[dimensionTitle] = {
        dimensionTitle,
        total: 0,
        count: 0,
        factors: {},
        dimensionId: dimensionObj.id - 1,
      };
    }
    dimensionMap[dimensionTitle].total += value;
    dimensionMap[dimensionTitle].count += 1;
    if (!dimensionMap[dimensionTitle].factors[factorTitle]) {
      dimensionMap[dimensionTitle].factors[factorTitle] = {
        total: 0,
        count: 0,
        factorId: factorObj.id - 1,
      };
    }
    dimensionMap[dimensionTitle].factors[factorTitle].total += value;
    dimensionMap[dimensionTitle].factors[factorTitle].count += 1;
  });

  let summaryArray = [];
  let dimensionIdCounter = 0;
  Object.values(dimensionMap).forEach((dim) => {
    const dimensionAverage = (dim.total / dim.count) * 10;
    let dimensionNorm = null;
    let dimensionDiff = null;
    if (norms && Array.isArray(norms.pillars)) {
      const dimensionPillars = norms.pillars.filter(
        (p) => p.id === dim.dimensionId
      );
      if (dimensionPillars.length > 0) {
        const sum = dimensionPillars.reduce((acc, p) => acc + p.average, 0);
        dimensionNorm = (sum / dimensionPillars.length) * 10;
        dimensionDiff = dimensionAverage - dimensionNorm;
      }
    }
    const factorsArray = Object.keys(dim.factors).map((factorTitle) => {
      const factorData = dim.factors[factorTitle];
      const factorAverage = (factorData.total / factorData.count) * 10;
      let normAverage = null;
      let difference = null;
      if (norms && Array.isArray(norms.pillars)) {
        const pillar = norms.pillars.find(
          (p) => p.id === dim.dimensionId && p.factor === factorData.factorId
        );
        if (pillar) {
          normAverage = pillar.average;
          difference = factorAverage - normAverage;
        }
      }
      return {
        factorTitle,
        average: factorAverage,
        normAverage,
        difference,
      };
    });
    summaryArray.push({
      id: dimensionIdCounter++,
      dimensionTitle: dim.dimensionTitle,
      dimensionId: dim.dimensionId,
      dimensionAverage,
      dimensionNorm,
      dimensionDiff,
      factors: factorsArray,
    });
  });

  // --- Build the question-level breakdown using processedBreakdown ---
  const questionBreakdown = buildQuestionBreakdown(
    processedBreakdown,
    questions,
    anchorCategory,
    norms
  );


  // --- Build the outcomes breakdown (using the anchor-based filter as before) ---
  let outcomeResponseMap = [];
  data.forEach((response) => {
    if (!response.employee_outcomes || !response.employee_outcomes.responses)
      return;
    if (!filterResponseBySelectedFilters(response, selectedFilters)) return;
    let category = response.categories.find((cat) => cat.id === anchor);
    if (!category || !filters.has(category.response)) return;
    let role = category.response;
    response.employee_outcomes.responses.forEach((r) => {
      // Skip 'N/A' responses
      if (r.response === 'N/A' || r.response === null || r.response === undefined) {
        return;
      }
      
      const rawScore = +r.response;
      const distribution = getBuckets([rawScore]);
      const outcomeKey = `${r.q}-${r.s}`;
      outcomeResponseMap.push({
        _outcomeKey: outcomeKey,
        average: rawScore,
        distribution,
        role,
        stdev: 0,
        n: 1,
        questionId: Number(r.q),
        sId: Number(r.s),
      });
    });
  });

  const outcomesBreakdown = buildOutcomesBreakdown(
    outcomeResponseMap,
    outcomeQuestions,
    anchorCategory,
    normsConst
  );

  return {
    responses: processedBreakdown, // breakdown (anchor–dependent)
    summary: summaryArray, // overall summary (anchor–independent)
    questionBreakdown,
    outcomesBreakdown,
  };
};

// ------------------------------------------------------
// Example helper functions (placeholders for your logic)
// ------------------------------------------------------
function checkReverseScore(question, questions) {
  // Handle 'N/A' responses
  if (question.response === 'N/A' || question.response === null || question.response === undefined) {
    return null; // This should be handled by the caller
  }
  
  // Example: if question.reverse is true, do something special
  if (question.reverse) {
    // Suppose scale is 0-10, then reverse would be `score = 10 - question.response`
    return 10 - question.response;
  }
  return question.response;
}

function getStandardDeviation(values) {
  // Filter out 'N/A' and invalid values
  const validValues = values.filter(val => 
    val !== 'N/A' && val !== null && val !== undefined && !isNaN(val)
  );
  
  if (!validValues.length) return 0;
  let avg = validValues.reduce((a, b) => a + b, 0) / (validValues.length);
  let variance =
    validValues.reduce((acc, val) => acc + Math.pow(val - avg, 2), 0) /
    validValues.length;
  return Math.sqrt(variance);
}

function getBuckets(values) {
  // Bucket distribution example
  // You might be bucketing 0-10 or 1-10, etc.
  // For demonstration, we just create an 11-length array:
  let dist = new Array(11).fill(0);
  values.forEach((val) => {
    // Skip 'N/A' and invalid values
    if (val === 'N/A' || val === null || val === undefined || isNaN(val)) {
      return;
    }
    const idx = Math.max(0, Math.min(10, val)); // clamp to [0..10]
    dist[idx]++;
  });
  return dist;
}
